/**
 * M3 — "Read the Numbers" (NovaTech practice) data + grading.
 *
 * Verbatim from the original prototype: the m3a primer (`CONTENT.m3a`, content.js L240-255),
 * the m3b NovaTech analysis (`CONTENT.m3b`, content.js L260-285), and the `NOVATECH` /
 * `NOVATECH_EXPECTED` statement (app.js L285-298, mirrored in `_data/companies.ts` as `NOVATECH`).
 *
 * M3 is STATIC for everyone — no branching, no personalization (EXPERIENCE-MODEL.md §4 / §7).
 * The screens render as state-driven templates off this module + the `NOVATECH` statement; the
 * numbers are never hardcoded in JSX.
 *
 *   • m3-reading (primer)  → READING_PRIMER (the 5 numbered explainer rows + pull-quote)
 *   • m3-activity          → STATEMENT_ROWS (table labels) + M3_RATIOS (3 graded ratios) + the
 *                            diagnosis reveal copy. Grading is ±1.0 tolerance vs NOVATECH.expected.
 *
 * Source of truth: docs/EXPERIENCE-MODEL.md §4.
 */

import { type IncomeStatement, NOVATECH } from './companies';

// ---------------------------------------------------------------------------
// Statement-cell coordinates — used both by the table render and by the ratio
// → source-cell highlight mapping (Fix 4 / the original boxed-cell behavior).
// ---------------------------------------------------------------------------

/** A single statement-table cell: which line it's on + which reported-year column. */
export interface CellRef {
  /** Which `IncomeStatement` row supplies the value. */
  row: keyof IncomeStatement;
  /** Index into `NOVATECH.years` (0 = FY2022 … 2 = FY2024). */
  yearIndex: number;
}

// ---------------------------------------------------------------------------
// Static screen copy (intro + reading primer + activity headings)
// ---------------------------------------------------------------------------

/** m3-intro framing copy (Figma 40:9431 — static screen copy, not in the original `CONTENT`). */
export const M3_INTRO = {
  pill: 'Module 3 of 4',
  headline: 'Read the numbers',
  subtitle: 'Every finance career starts with one document: the income statement.',
  chips: ['A 30-second primer on how it works', "Then you'll read three ratios off a real one"],
} as const;

/** m3-reading primer copy (Figma 40:9457 — verbatim from `CONTENT.m3a`). */
export const READING_PRIMER = {
  headline: "What's an income statement?",
  body: 'An income statement answers one question: did this business make money over a period of time, and how?',
  /** The 5 numbered explainer rows (label + hint). */
  rows: [
    { label: 'Revenue', hint: 'What customers paid us' },
    { label: 'Cost of Goods Sold', hint: 'What it cost to deliver' },
    { label: 'Gross Profit', hint: 'Before paying overhead' },
    { label: 'Operating Expenses', hint: 'Sales, marketing, R&D, G&A' },
    { label: 'Operating Income', hint: 'After running the business' },
  ],
  pullQuote: 'The percentages matter more than the raw numbers.',
  modulePill: 'Module 3 · Read the numbers',
} as const;

/** m3-activity static headings/copy (Figma 40:9497 — verbatim from `CONTENT.m3b`). */
export const ACTIVITY_COPY = {
  headline: NOVATECH.name,
  subhead:
    "A fictional B2B software company, a quick warm-up before the real thing. Same statement structure you'd see for any real one.",
  tableTag: NOVATECH.chip, // "Practice example"
  ratiosHeading: 'Pick the Right Ratio for FY2024',
  ratiosHelper: 'Three ratios. Each has one correct answer. Pick from the options.',
  diagnosisHeading: 'Diagnose in one sentence',
  diagnosisPlaceholder: "In plain English, what is NovaTech's biggest financial challenge?",
  diagnosisMaxChars: 500,
  revealCta: 'Reveal the answer',
  diagnosisSample:
    'Operating expenses are outpacing revenue growth. The business is growing top-line but not yet scaling efficiently.',
  revealLabel: 'The challenge:',
  revealBody:
    'NovaTech is growing revenue (+37.5%) and improving gross margins (45% → 51%), but operating expenses (SG&A) are growing faster than revenue: $12M → $21M while revenue only grew $40M → $55M. Operating income is flat ($6M → $7M) despite 37.5% revenue growth. The business is not yet scaling efficiently. Until SG&A growth slows, every new dollar of revenue is being eaten by overhead. This is one of the most common stories in growing companies, and spotting it early is exactly what finance analysts get paid to do.',
  feedbackNailedIt: 'Nailed it.',
  feedbackTryAnother: 'Not quite. Try another.',
  modulePill: 'Module 3 · Read the numbers',
} as const;

// ---------------------------------------------------------------------------
// Statement table rows — labels paired to the NOVATECH statement lines.
// ---------------------------------------------------------------------------

/** A statement-table line: the display label + which `IncomeStatement` key supplies its 3 values. */
export interface StatementRowDef {
  key: keyof IncomeStatement;
  label: string;
}

/**
 * The 5 NovaTech statement rows in display order (Figma 40:9514-40:9540).
 * Labels match Vinicius's table verbatim ("Cost of Revenue", not "Cost of Goods Sold" — the
 * reading primer uses the longer phrasing; the activity table uses the shorter one).
 */
export const STATEMENT_ROWS: readonly StatementRowDef[] = [
  { label: 'Revenue', key: 'revenue' },
  { label: 'Cost of Revenue', key: 'cogs' },
  { label: 'Gross Profit', key: 'grossProfit' },
  { label: 'Operating Expenses', key: 'opex' },
  { label: 'Operating Income', key: 'opIncome' },
] as const;

// ---------------------------------------------------------------------------
// Practice ratios — 3 multiple-choice questions, graded vs NOVATECH.expected.
// ---------------------------------------------------------------------------

export type RatioKey = 'revGrowth' | 'grossMargin' | 'opMargin';

export interface Ratio {
  /** The single correct option value (== NOVATECH.expected[key]). */
  correct: number;
  /** The arithmetic the analyst is computing, shown under the label. */
  formula: string;
  key: RatioKey;
  /** Ratio name without the period parenthetical, e.g. "Revenue Growth". */
  name: string;
  /** All answer options (one is correct; positions get shuffled per visit). */
  options: readonly number[];
  /**
   * The period parenthetical, e.g. "(2 year)" / "(FY2024)" — rendered on its own line so it never
   * breaks mid-phrase (e.g. "Revenue Growth (2 / year)"). Includes the parentheses.
   */
  period: string;
  /**
   * The statement cells this ratio is computed from (Fix 4) — highlighted in the NovaTech table
   * when this ratio is the active/engaged one. Derived from each `formula` above.
   */
  sources: readonly CellRef[];
}

/**
 * The 3 practice ratios (verbatim from `CONTENT.m3b.ratios` / app.js `NOVATECH_EXPECTED`).
 * `correct` is sourced from `NOVATECH.expected` so the answer key can never drift from the data
 * the table renders. Options are stored in their canonical order; `shuffleOptions` randomizes
 * positions per visit so the answer isn't always in the same slot (EXPERIENCE-MODEL.md §4).
 */
export const M3_RATIOS: readonly Ratio[] = [
  {
    key: 'revGrowth',
    name: 'Revenue Growth',
    period: '(2 year)',
    formula: '(55 − 40) ÷ 40 × 100',
    options: [27.3, 37.5, 45.5],
    correct: NOVATECH.expected.revGrowth, // 37.5
    // (55 − 40) ÷ 40 → Revenue FY2022 ($40M) + Revenue FY2024 ($55M).
    sources: [
      { row: 'revenue', yearIndex: 0 },
      { row: 'revenue', yearIndex: 2 },
    ],
  },
  {
    key: 'grossMargin',
    name: 'Gross Margin',
    period: '(FY2024)',
    formula: '28 ÷ 55 × 100',
    options: [42.1, 50.9, 58.3],
    correct: NOVATECH.expected.grossMargin, // 50.9
    // 28 ÷ 55 → Gross Profit FY2024 ($28M) + Revenue FY2024 ($55M).
    sources: [
      { row: 'grossProfit', yearIndex: 2 },
      { row: 'revenue', yearIndex: 2 },
    ],
  },
  {
    key: 'opMargin',
    name: 'Operating Margin',
    period: '(FY2024)',
    formula: '7 ÷ 55 × 100',
    options: [8.4, 12.7, 17.5],
    correct: NOVATECH.expected.opMargin, // 12.7
    // 7 ÷ 55 → Operating Income FY2024 ($7M) + Revenue FY2024 ($55M).
    sources: [
      { row: 'opIncome', yearIndex: 2 },
      { row: 'revenue', yearIndex: 2 },
    ],
  },
] as const;

/** ±tolerance used to grade a picked ratio value against `correct` (matches the original). */
export const RATIO_TOLERANCE = 1.0;

/** True if a picked option value is the correct answer for the ratio (±RATIO_TOLERANCE). */
export function isRatioCorrect(ratio: Ratio, value: number): boolean {
  return Math.abs(value - ratio.correct) <= RATIO_TOLERANCE;
}

/**
 * Return a per-visit shuffled copy of a ratio's options (Fisher–Yates over a seedable RNG).
 * The original shuffles option positions on each visit so the correct answer isn't always in the
 * middle slot. `rng` defaults to Math.random; tests pass a deterministic generator.
 */
export function shuffleOptions(options: readonly number[], rng: () => number = Math.random): number[] {
  const out = [...options];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i] as number;
    out[i] = out[j] as number;
    out[j] = tmp;
  }
  return out;
}

/**
 * Whether the activity can advance — all 3 ratios answered correctly OR the answer was revealed
 * (`canAdvance('m3b')` in the original, app.js). `correctKeys` is the set of ratio keys the user
 * has gotten right so far.
 */
export function canAdvanceM3(correctKeys: ReadonlySet<RatioKey>, revealed: boolean): boolean {
  return revealed || M3_RATIOS.every((r) => correctKeys.has(r.key));
}
